<template>
  <custom-fields-list
    item-class="d-flex flex-row gap-2"
    list-class="v-row d-flex flex-column gap-4"
    :fields="customFields"
    :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"
    :timezone="warehouse.timezone"
    :warehouse="warehouse"
    @change="propagateValueChange" />
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { isArray, isNil } from 'lodash';
import {
  castField,
  CustomFieldType,
  CustomFormDataEntitiesEnum,
  filterActiveFormFields
} from '@nova/core';
import { useCustomForms } from '@satellite/composables/useCustomForms';
import { provideCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';
import CustomFieldsList from '@satellite/components/custom_forms/CustomFieldsList.vue';

export default defineComponent({
  name: 'CreateAppointmentCustomForms',
  components: { CustomFieldsList },
  emits: ['update:value'],
  props: {
    triggers: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    },
    initialCustomFormsData: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const { getCustomField } = useCustomForms();

    /** A warehouse-configured document field can render here, and injects this to upload. */
    provideCustomFormsStorageContext(() => props.warehouse?.id);
    const localFields = ref([]);

    /** The list edits the custom field in place, so the answer is read back off it. */
    const customFields = computed(() => localFields.value.map(({ customField }) => customField));

    const propagateValueChange = () =>
      emit(
        'update:value',
        localFields.value.map(field => ({
          formFieldId: field.formFieldId,
          triggerId: field.triggerId,
          value: field.customField.value
        }))
      );

    const parseClonedFieldValue = (rawValue, fieldType) => {
      if (isNil(rawValue)) {
        return fieldType === CustomFieldType.Bool ? false : null;
      }
      // Values may already be arrays (e.g. multidoc/dropdownmultiselect after
      // upstream normalization); pass them through without re-parsing.
      if (isArray(rawValue)) {
        return rawValue;
      }
      // castField expects a string, but the API may return numeric values for
      // Number (int) fields because the @AfterLoad hook already casts them.
      // Convert to string so castField can process every type uniformly.
      const casted = castField(fieldType, String(rawValue));
      // castField returns null for bool values it can't parse; default to false.
      if (fieldType === CustomFieldType.Bool) {
        return casted ?? false;
      }
      // When castField for a multi-value type returns a non-array (e.g. a plain URL string
      // from a single-doc field that was converted to multidoc, or a single dropdown value
      // converted to a multiselect), wrap it so the upload/multiselect widgets can use it.
      if (
        (fieldType === CustomFieldType.MultiDocument ||
          fieldType === CustomFieldType.DropDownMultiSelect) &&
        !isNil(casted) &&
        !isArray(casted)
      ) {
        return [String(casted)];
      }
      return casted;
    };

    const setLocalFields = () => {
      props.triggers
        .filter(trigger => Boolean(trigger.flow?.formFrom))
        .flatMap(trigger =>
          filterActiveFormFields(trigger.flow.formFrom.formFields)
            .sort((firstField, secondField) => firstField.order - secondField.order)
            .map(formField => ({ trigger, formField }))
        )
        .forEach(({ trigger, formField }) => {
          const fieldType = formField.field.type;
          const existingData = props.initialCustomFormsData.find(
            d => d.formFieldId === formField.id && d.triggerId === trigger.id
          );
          const defaultValue = fieldType === CustomFieldType.Bool ? false : null;
          const initialValue =
            existingData != null
              ? parseClonedFieldValue(existingData.value, fieldType)
              : defaultValue;

          const customField = getCustomField(formField);
          customField.value = initialValue;

          localFields.value.push({
            form: trigger.flow.formFrom,
            customField,
            entityName: CustomFormDataEntitiesEnum.appointment,
            formFieldId: formField.id,
            triggerId: trigger.id
          });
        });
    };

    // Build the initial value for the request
    onMounted(() => {
      setLocalFields();
      propagateValueChange();
    });

    watch(localFields, () => propagateValueChange(), { deep: true });

    return {
      propagateValueChange,
      localFields,
      customFields
    };
  }
});
</script>
